import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DelayByShift } from "../models/DelayByShift";
import {
	deleteDelay as deleteDelayApi,
	justifyDelay as justifyDelayApi,
	splitDelay as splitDelayApi,
	upsertDelay as upsertDelayApi,
} from "../repositories/DelaysManagerRepository";
import { DELAYS_QUERY_KEYS } from "./useDelays";

interface MutationOptions {
	onSuccess?: () => void;
	onError?: (error: Error) => void;
}

interface JustifyDelayParams {
	delayId: number;
	delayCodeId: number | null;
	delayCategoryId: number | null;
	comments: string | null;
}

export const useJustifyDelay = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (params: JustifyDelayParams) => {
			const response = await justifyDelayApi(
				params.delayId,
				params.delayCodeId,
				params.delayCategoryId,
				params.comments,
			);
			if (!response.ok) {
				throw new Error(response.message || "Error justifying delay");
			}
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: DELAYS_QUERY_KEYS.all });
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		justifyDelay: mutation.mutate,
		justifyDelayAsync: mutation.mutateAsync,
		isJustifying: mutation.isPending,
	};
};

interface UpsertDelayParams {
	delayId: number | null;
	assetId: number | null;
	start: Date | null;
	end: Date | null;
	delayCodeId: number | null;
	delayCategoryId: number | null;
	comments: string | null;
}

export const useUpsertDelay = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (params: UpsertDelayParams) => {
			const response = await upsertDelayApi(
				params.delayId,
				params.assetId,
				params.start,
				params.end,
				params.delayCodeId,
				params.delayCategoryId,
				params.comments,
			);
			if (!response.ok) {
				throw new Error(response.message || "Error saving delay");
			}
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: DELAYS_QUERY_KEYS.all });
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertDelay: mutation.mutate,
		upsertDelayAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};

export const useDeleteDelay = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (delayId: number) => {
			const response = await deleteDelayApi(delayId);
			if (!response.ok) {
				throw new Error(response.message || "Error deleting delay");
			}
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: DELAYS_QUERY_KEYS.all });
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteDelay: mutation.mutate,
		deleteDelayAsync: mutation.mutateAsync,
		isDeleting: mutation.isPending,
	};
};

interface SplitDelayParams {
	delay1: DelayByShift;
	delay2: DelayByShift;
}

export const useSplitDelay = ({ onSuccess, onError }: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (params: SplitDelayParams) => {
			const response = await splitDelayApi(params.delay1, params.delay2);
			if (!response.ok) {
				throw new Error(response.message || "Error splitting delay");
			}
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: DELAYS_QUERY_KEYS.all });
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		splitDelay: mutation.mutate,
		splitDelayAsync: mutation.mutateAsync,
		isSplitting: mutation.isPending,
	};
};
